import { CanvasTexture, SRGBColorSpace } from 'three'
import { aparenciaRotulo, numeroNoRotulo, type Pictograma } from '../../dominio/produtos'

/**
 * Painel de segurança e rótulo de risco desenhados num canvas.
 *
 * Texto em malha (troika/`<Text>` do drei) baixaria fonte da rede, e a aula
 * pode não ter rede; o canvas 2D usa a fonte do sistema e vira textura na hora.
 * Fora do navegador (testes em Node) não há `document`: devolve `null` e o 3D
 * pinta só a cor de fundo.
 */

const temCanvas = (): boolean => typeof document !== 'undefined' && typeof document.createElement === 'function'

function novaTextura(largura: number, altura: number, desenhar: (ctx: CanvasRenderingContext2D) => void): CanvasTexture | null {
  if (!temCanvas()) return null
  const canvas = document.createElement('canvas')
  canvas.width = largura
  canvas.height = altura
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  desenhar(ctx)
  const textura = new CanvasTexture(canvas)
  textura.colorSpace = SRGBColorSpace
  textura.anisotropy = 4
  return textura
}

/** Painel laranja 40×30: número de risco em cima, número ONU embaixo, bordas pretas. */
export function texturaPainelSeguranca(numeroRisco: string, onu: number): CanvasTexture | null {
  return novaTextura(400, 300, (ctx) => {
    ctx.fillStyle = '#f07c00'
    ctx.fillRect(0, 0, 400, 300)
    ctx.strokeStyle = '#111'
    ctx.lineWidth = 16
    ctx.strokeRect(8, 8, 384, 284)
    ctx.beginPath()
    ctx.moveTo(8, 150)
    ctx.lineTo(392, 150)
    ctx.stroke()
    ctx.fillStyle = '#111'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = 'bold 104px "Arial Narrow", Arial, Helvetica, sans-serif'
    ctx.fillText(numeroRisco, 200, 84)
    ctx.fillText(String(onu).padStart(4, '0'), 200, 222)
  })
}

function desenharPictograma(ctx: CanvasRenderingContext2D, pictograma: Pictograma, cor: string) {
  ctx.fillStyle = cor
  ctx.strokeStyle = cor
  ctx.lineWidth = 10
  ctx.lineJoin = 'round'
  const cx = 256
  const cy = 170
  switch (pictograma) {
    case 'chama':
    case 'chamaSobreCirculo': {
      if (pictograma === 'chamaSobreCirculo') {
        ctx.beginPath()
        ctx.arc(cx, cy + 50, 42, 0, Math.PI * 2)
        ctx.lineWidth = 16
        ctx.stroke()
      }
      ctx.beginPath()
      ctx.moveTo(cx, cy - 95)
      ctx.bezierCurveTo(cx + 70, cy - 30, cx + 75, cy + 30, cx + 40, cy + 65)
      ctx.bezierCurveTo(cx + 45, cy + 20, cx + 20, cy - 5, cx + 5, cy - 15)
      ctx.bezierCurveTo(cx + 10, cy + 20, cx - 5, cy + 45, cx - 20, cy + 65)
      ctx.bezierCurveTo(cx - 55, cy + 40, cx - 70, cy - 20, cx, cy - 95)
      ctx.fill()
      ctx.fillRect(cx - 80, cy + 72, 160, 14)
      break
    }
    case 'caveira': {
      ctx.beginPath()
      ctx.ellipse(cx, cy - 25, 55, 50, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillRect(cx - 32, cy + 10, 64, 32)
      ctx.fillStyle = '#ffffff'
      ctx.beginPath()
      ctx.arc(cx - 22, cy - 25, 15, 0, Math.PI * 2)
      ctx.arc(cx + 22, cy - 25, 15, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = cor
      ctx.lineWidth = 18
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(cx - 85, cy + 45)
      ctx.lineTo(cx + 85, cy + 95)
      ctx.moveTo(cx + 85, cy + 45)
      ctx.lineTo(cx - 85, cy + 95)
      ctx.stroke()
      break
    }
    case 'cilindro': {
      ctx.fillRect(cx - 30, cy - 70, 60, 150)
      ctx.beginPath()
      ctx.arc(cx, cy - 70, 30, Math.PI, 0)
      ctx.fill()
      break
    }
    case 'corrosao': {
      // dois tubos de ensaio pingando — sobre a mão e o metal, simplificados
      ctx.fillRect(cx - 90, cy - 60, 30, 70)
      ctx.fillRect(cx + 60, cy - 60, 30, 70)
      ctx.beginPath()
      ctx.arc(cx - 75, cy + 30, 8, 0, Math.PI * 2)
      ctx.arc(cx + 75, cy + 30, 8, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillRect(cx - 110, cy + 55, 80, 16)
      ctx.fillRect(cx + 30, cy + 55, 80, 16)
      break
    }
    case 'trifolio': {
      ctx.beginPath()
      ctx.arc(cx, cy, 14, 0, Math.PI * 2)
      ctx.fill()
      for (let i = 0; i < 3; i++) {
        const inicio = -Math.PI / 2 + i * ((2 * Math.PI) / 3) - Math.PI / 6
        ctx.beginPath()
        ctx.moveTo(cx, cy)
        ctx.arc(cx, cy, 80, inicio, inicio + Math.PI / 3)
        ctx.arc(cx, cy, 24, inicio + Math.PI / 3, inicio, true)
        ctx.fill()
      }
      break
    }
    case 'explosao': {
      for (let i = 0; i < 12; i++) {
        const angulo = (i / 12) * Math.PI * 2
        ctx.beginPath()
        ctx.moveTo(cx, cy)
        ctx.lineTo(cx + Math.cos(angulo) * 90, cy + Math.sin(angulo) * 90)
        ctx.stroke()
      }
      break
    }
    case 'listras': {
      ctx.save()
      ctx.globalCompositeOperation = 'source-atop'
      for (let x = -512; x < 512; x += 64) {
        ctx.fillStyle = '#d7261e'
        ctx.fillRect(x, 0, 32, 512)
      }
      ctx.restore()
      break
    }
  }
}

/**
 * Rótulo de risco (losango): a textura é o quadrado que, girado 45°, vira o
 * losango; os cantos ficam transparentes.
 */
export function texturaRotuloRisco(classe: string): CanvasTexture | null {
  const aparencia = aparenciaRotulo(classe)
  return novaTextura(512, 512, (ctx) => {
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(256, 6)
    ctx.lineTo(506, 256)
    ctx.lineTo(256, 506)
    ctx.lineTo(6, 256)
    ctx.closePath()
    ctx.clip()
    ctx.fillStyle = aparencia.corSuperior
    ctx.fillRect(0, 0, 512, 256)
    ctx.fillStyle = aparencia.corInferior
    ctx.fillRect(0, 256, 512, 256)
    desenharPictograma(ctx, aparencia.pictograma, aparencia.corSimbolo)
    // borda interna, a ~5 mm da aresta no rótulo real
    ctx.strokeStyle = aparencia.corInferior === '#111111' ? '#ffffff' : aparencia.corSimbolo
    ctx.lineWidth = 6
    ctx.beginPath()
    ctx.moveTo(256, 30)
    ctx.lineTo(482, 256)
    ctx.lineTo(256, 482)
    ctx.lineTo(30, 256)
    ctx.closePath()
    ctx.stroke()
    ctx.fillStyle = aparencia.corInferior === '#111111' ? '#ffffff' : aparencia.corSimbolo
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = 'bold 88px Arial, Helvetica, sans-serif'
    ctx.fillText(numeroNoRotulo(classe), 256, 420)
    ctx.restore()
  })
}
